/* ========================================
   Popover UA resets — unlayered so they unconditionally beat
   the browser's [popover] UA stylesheet (padding / inset / border / margin).
   Must NOT be inside @layer; unlayered author rules win over every layer and
   every UA default.
   ======================================== */
.tooltip {
  inset: unset;
  padding: calc(var(--size-0-5) / 2) var(--size-2) var(--size-0-5);
  margin: 0;
  overflow: visible;
  border: var(--_tooltip-border-width, var(--border-0)) solid var(--_tooltip-border-color, transparent);
  transform-origin: center;
}

@layer refine.base {
  :host {
    position: relative;
    display: inline-block;
  }

  .tooltip {
    --_tooltip-fg: var(--color-contrast-100);
    --_tooltip-bg: color-mix(in oklch, var(--color-contrast-900) 80%, transparent);
    --_tooltip-border-width: var(--border);
    --_tooltip-border-color: color-mix(in oklch, var(--color-contrast-100) 12%, transparent);
    --_tooltip-shadow: var(--shadow-md);
    --_tooltip-closed-transform: translateY(4px) scale(0.98);

    /* Positioning — JS sets top/left inline */
    position: fixed;
    max-width: var(--tooltip-max-width, var(--size-72));
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    line-height: var(--leading-normal);
    color: var(--_tooltip-fg);
    word-break: auto-phrase;
    white-space: normal;
    pointer-events: none;
    background: var(--_tooltip-bg);
    border-radius: var(--tooltip-radius, var(--rounded-lg));
    box-shadow: var(--_tooltip-shadow);

    /* Hidden by default (also serves as exit state) */
    opacity: 0;
    backdrop-filter: blur(var(--blur-sm));
    transform: var(--_tooltip-closed-transform);
    transition:
      opacity var(--transition-fast),
      transform var(--transition-fast),
      display var(--transition-fast) allow-discrete,
      overlay var(--transition-fast) allow-discrete;
  }

  @media (prefers-color-scheme: dark) {
    .tooltip {
      --_tooltip-fg: var(--color-contrast-900);
      --_tooltip-bg: color-mix(in oklch, var(--color-contrast-100) 80%, transparent);
      --_tooltip-border-color: color-mix(in oklch, var(--color-contrast-900) 12%, transparent);
    }
  }

  .tooltip-text {
    display: block;
    white-space: pre-line;
  }

  .tooltip[data-placement='top'] {
    --_tooltip-closed-transform: translateY(4px) scale(var(--overlay-enter-scale-dialog));
  }

  .tooltip[data-placement='bottom'] {
    --_tooltip-closed-transform: translateY(-4px) scale(var(--overlay-enter-scale-dialog));
  }

  .tooltip[data-placement='left'] {
    --_tooltip-closed-transform: translateX(4px) scale(var(--overlay-enter-scale-dialog));
  }

  .tooltip[data-placement='right'] {
    --_tooltip-closed-transform: translateX(-4px) scale(var(--overlay-enter-scale-dialog));
  }

  .tooltip[data-open] {
    display: block;
  }

  /* Entry animation via @starting-style */
  .tooltip:popover-open,
  .tooltip[data-open] {
    opacity: 1;
    transform: translate(0, 0) scale(1);
    transition:
      opacity var(--transition-normal),
      transform var(--transition-spring),
      display var(--transition-normal) allow-discrete,
      overlay var(--transition-normal) allow-discrete;
    will-change: opacity, transform;

    @starting-style {
      opacity: 0;
      transform: var(--_tooltip-closed-transform);
    }
  }
}

@layer refine.variants {
  /* Light tooltip */
  :host([variant='light']) .tooltip {
    --_tooltip-fg: var(--color-contrast-900);
    --_tooltip-bg: color-mix(in oklch, var(--color-contrast-100) 80%, transparent);
    --_tooltip-border-color: color-mix(in oklch, var(--color-contrast-900) 12%, transparent);
    --_tooltip-shadow: var(--shadow-md);
  }

  @media (prefers-color-scheme: dark) {
    :host([variant='light']) .tooltip {
      --_tooltip-fg: var(--color-contrast-100);
      --_tooltip-bg: color-mix(in oklch, var(--color-contrast-900) 80%, transparent);
      --_tooltip-border-color: color-mix(in oklch, var(--color-contrast-100) 12%, transparent);
    }
  }
}

@layer refine.utilities {
  :host([size='sm']) .tooltip {
    font-size: var(--text-xs);
  }

  :host([size='lg']) .tooltip {
    font-size: var(--text-base);
  }
}
